import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from './api';

const RecordingContext = createContext(null);

export function RecordingProvider({ children }) {
  const [pendingJobs, setPendingJobs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('recording_jobs') || '[]'); } catch { return []; }
  });
  const [notifications, setNotifications] = useState([]);
  const pollingRef = useRef(null);

  // Persist jobs to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('recording_jobs', JSON.stringify(pendingJobs));
  }, [pendingJobs]);

  // Global poll — runs regardless of which page is active
  useEffect(() => {
    const processing = pendingJobs.filter(j => j.status === 'processing');
    if (processing.length === 0) return;

    pollingRef.current = setInterval(async () => {
      let changed = false;
      const updated = [...pendingJobs];

      for (const job of processing) {
        try {
          const res = await api.get(`/api/jobs/${job.job_id}`);
          if (!res.data.success) {
            // 404 — job expired from KV; if it's been > 15 min, remove it silently
            const ageMs = Date.now() - new Date(job.started_at || 0).getTime();
            if (ageMs > 15 * 60 * 1000) {
              const idx = updated.findIndex(j => j.job_id === job.job_id);
              if (idx >= 0) { updated[idx] = { ...updated[idx], status: 'failed', error: 'Job status expired — check the Jobs panel or journal directly.' }; changed = true; }
            }
            continue;
          }
          const serverJob = res.data.job;
          if (serverJob.status !== job.status) {
            const idx = updated.findIndex(j => j.job_id === job.job_id);
            if (idx >= 0) {
              updated[idx] = { ...updated[idx], ...serverJob, job_id: job.job_id };
              changed = true;
              if (serverJob.status === 'done') {
                setNotifications(prev => [{
                  id: job.job_id,
                  type: 'done',
                  title: 'Transcription ready',
                  body: job.name,
                  tracker_node_id: job.tracker_node_id,
                  tracker_node_title: job.tracker_node_title,
                  journal_id: serverJob.journal_id,
                  read: false,
                  created_at: new Date().toISOString(),
                }, ...prev]);
              } else if (serverJob.status === 'failed') {
                setNotifications(prev => [{
                  id: job.job_id,
                  type: 'failed',
                  title: 'Transcription failed',
                  body: job.name,
                  read: false,
                  created_at: new Date().toISOString(),
                }, ...prev]);
              }
            }
          }
        } catch {}
      }

      if (changed) setPendingJobs(updated);
    }, 5000);

    return () => clearInterval(pollingRef.current);
  }, [pendingJobs]);

  function addJob(jobId, name, trackerNodeId, trackerNodeTitle) {
    const job = {
      job_id: jobId, name,
      tracker_node_id: trackerNodeId,
      tracker_node_title: trackerNodeTitle,
      status: 'processing',
      started_at: new Date().toISOString(),
    };
    setPendingJobs(prev => [job, ...prev]);
  }

  function removeJob(jobId) {
    setPendingJobs(prev => prev.filter(j => j.job_id !== jobId));
  }

  function markAllRead() {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }

  return (
    <RecordingContext.Provider value={{ pendingJobs, notifications, addJob, removeJob, markAllRead }}>
      {children}
    </RecordingContext.Provider>
  );
}

export function useRecording() {
  return useContext(RecordingContext);
}
